import React from 'react';
import { useSelector } from 'react-redux';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { Content } from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchPropertyResearchSubLandingPageData } from '../../common/helpers/data/landingPages/fetchPropertyResearchLandingPageData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import { getLandingPageType } from '../../common/helpers/domain/landingPages';
import {
  PostSubcategory,
  PropertyResearchPostCategory,
} from '../../common/helpers/domain/postCategories';
import { parsePropertyResearchPostCategory } from '../../common/helpers/routing/landingPageUrlParser';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getPropertyTypeTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostList } from '../../components/global/PostList/PostList';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generatePostSubcategoryBreadcrumbList } from '../../components/landingPage/breadcrumb';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import {
  DynamicComponentForArticleFeedModule,
  DynamicComponentForFeaturedVideos,
  DynamicComponentForHeroVideo,
  DynamicComponentForInPageSearch,
  DynamicComponentForLocationRecommendation,
} from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { RecommendedLocation } from '../../queries/homePage/locationRecommendation';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { updatePageType } from '../../store/home/slice';
import ViewMoreLink from '../../components/ViewMoreLink/ViewMoreLink';
import { PropertyType } from '../../common/helpers/domain/propertyTypes';
import PostListModule from '../../components/landingPage/PostListModule';
import { getComboAnalyticsFeature } from '../../common/analytics/helpers/analyticsFeatureHelper';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { WholeState } from '../../store/common/type';
import {
  trackViewMoreLink,
  trackHeroArticle,
  trackMostPopularArticle,
  trackArticle,
  trackHeroVideoArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackRecommendedLocationCard,
  trackRecommendedLocationCarouselArrow,
} from '../../common/analytics/analyticsTracker';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import { getAdSectByPageType } from '../../common/helpers/ads/AdsHelper';

interface PropertyResearchSubLandingPageProps {
  canonicalLink: string;
  postCategory: PropertyResearchPostCategory;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
  condoPosts: Content[];
  housePosts: Content[];
  townhomePosts: Content[];
  heroVideo?: HeroVideoInfo;
  featuredVideos?: Content[];
  recommendedLocations: RecommendedLocation[];
}

function PropertyResearchSubLandingPage({
  canonicalLink,
  heroArticle,
  popularArticles,
  postCategory,
  condoPosts,
  housePosts,
  townhomePosts,
  heroVideo,
  featuredVideos,
  recommendedLocations,
}: PropertyResearchSubLandingPageProps) {
  const moduleResource = commonResource.postModules;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const landingPageType = getLandingPageType([postCategory]);
  const analyticsSource = generateEventSourceByPageType(landingPageType);

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader pageType={landingPageType} canonicalLink={canonicalLink} />
      <Breadcrumb items={generatePostSubcategoryBreadcrumbList(postCategory)} />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title[postCategory]}
          subtitle={homeResource.subtitle[postCategory]}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(popularArticle) =>
                trackMostPopularArticle(
                  userInfo,
                  popularArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      {condoPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="condo"
        >
          <PostListModule
            title={moduleResource[postCategory]?.condo.title}
            subtitle={moduleResource[postCategory]?.condo.subtitle}
            viewMoreLink={
              postCategory !== PostSubcategory.PointOfView && (
                <ViewMoreLink
                  landingPageType={getLandingPageType(
                    [postCategory],
                    PropertyType.Condominium,
                  )}
                  onClick={(targetLandingPage) => {
                    trackViewMoreLink(
                      userInfo,
                      analyticsSource,
                      generateEventSourceByPageType(targetLandingPage),
                      getComboAnalyticsFeature({
                        postSubCategory: postCategory,
                        propertyType: PropertyType.Condominium,
                      }),
                    );
                  }}
                />
              )
            }
          >
            <PostList
              contents={condoPosts}
              adUnit={AdUnits.HP_Pos1}
              adSect={getAdSectByPageType(landingPageType)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  getComboAnalyticsFeature({
                    postSubCategory: postCategory,
                    propertyType: PropertyType.Condominium,
                  }),
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {housePosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="house"
        >
          <PostListModule
            title={moduleResource[postCategory]?.house.title}
            subtitle={moduleResource[postCategory]?.house.subtitle}
            viewMoreLink={
              postCategory !== PostSubcategory.PointOfView && (
                <ViewMoreLink
                  landingPageType={getLandingPageType(
                    [postCategory],
                    PropertyType.House,
                  )}
                  onClick={(targetLandingPage) => {
                    trackViewMoreLink(
                      userInfo,
                      analyticsSource,
                      generateEventSourceByPageType(targetLandingPage),
                      getComboAnalyticsFeature({
                        postSubCategory: postCategory,
                        propertyType: PropertyType.House,
                      }),
                    );
                  }}
                />
              )
            }
          >
            <PostList
              contents={housePosts}
              adUnit={AdUnits.HP_Pos2}
              adSect={getAdSectByPageType(landingPageType)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  getComboAnalyticsFeature({
                    postSubCategory: postCategory,
                    propertyType: PropertyType.House,
                  }),
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {townhomePosts.length > 0 &&
        postCategory !== PostSubcategory.PointOfView && (
          <Row
            verticalSpacing={homePageRowSetting.vertical}
            maxWidth={homePageRowSetting.maxWidth}
            dataTest="townhome"
          >
            <PostListModule
              title={moduleResource[postCategory]?.townhome.title}
              subtitle={moduleResource[postCategory]?.townhome.subtitle}
              viewMoreLink={
                <ViewMoreLink
                  landingPageType={getLandingPageType(
                    [postCategory],
                    PropertyType.Townhome,
                  )}
                  onClick={(targetLandingPage) => {
                    trackViewMoreLink(
                      userInfo,
                      analyticsSource,
                      generateEventSourceByPageType(targetLandingPage),
                      getComboAnalyticsFeature({
                        postSubCategory: postCategory,
                        propertyType: PropertyType.Townhome,
                      }),
                    );
                  }}
                />
              }
            >
              <PostList
                contents={townhomePosts}
                adUnit={AdUnits.HP_Pos3}
                adSect={getAdSectByPageType(landingPageType)}
                onClickPostCard={(post) =>
                  trackArticle(
                    userInfo,
                    post,
                    analyticsSource,
                    getComboAnalyticsFeature({
                      postSubCategory: postCategory,
                      propertyType: PropertyType.Townhome,
                    }),
                  )
                }
              />
            </PostListModule>
          </Row>
        )}
      {(heroVideo || featuredVideos.length > 0) &&
        postCategory === PostSubcategory.Review && (
          <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
            <div style={{ overflowX: 'hidden' }}>
              <DynamicComponentForHeroVideo
                heroVideo={heroVideo}
                onClick={(heroVideo) =>
                  trackHeroVideoArticle(
                    userInfo,
                    heroVideo,
                    analyticsSource,
                    ANALYTICS_FEATURES.HERO_VIDEO,
                  )
                }
              />
              <DynamicComponentForFeaturedVideos
                contents={featuredVideos}
                onClickVideoCard={(post) =>
                  trackArticle(
                    userInfo,
                    post,
                    analyticsSource,
                    ANALYTICS_FEATURES.VIDEO,
                  )
                }
              />
            </div>
          </Row>
        )}
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard2}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      {recommendedLocations.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle
            title={homeResource.locationRecommendation.title}
            subtitle={homeResource.locationRecommendation.subtitle}
          />
          <DynamicComponentForLocationRecommendation
            locations={recommendedLocations}
            onClickRecommendedLocationCarouselArrow={() =>
              trackRecommendedLocationCarouselArrow(
                userInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_HIGHLIGHT,
              )
            }
            onClickRecommendedLocationCard={(locationInfo) =>
              trackRecommendedLocationCard(
                userInfo,
                locationInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_HIGHLIGHT,
              )
            }
          />
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <DynamicComponentForInPageSearch />
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard3}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getPropertyTypeTabDefinition([postCategory])}
          excludePostIds={extractPostIds(
            [...condoPosts, ...housePosts, ...townhomePosts, ...featuredVideos],
            heroVideo,
          )}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(landingPageType)}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

PropertyResearchSubLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
  query,
}: CustomNextPageContext): Promise<PropertyResearchSubLandingPageProps> => {
  const canonicalLink = config.baseUrl + asPath.substring(1);
  const postCategory = parsePropertyResearchPostCategory(
    query.category as string,
  ) as
    | PostSubcategory.Review
    | PostSubcategory.Preview
    | PostSubcategory.PointOfView;

  try {
    const { heroArticle, mostPopular, recommendedLocations } =
      await fetchLandingPageTopContentData(apolloClient, {
        postCategories: [postCategory],
      });

    const { condo, house, townhome, video } =
      await fetchPropertyResearchSubLandingPageData(
        apolloClient,
        heroArticle?.id ? [heroArticle.id] : [],
        postCategory,
      );

    const landingPageType = getLandingPageType([postCategory]);
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );

    return {
      postCategory,
      canonicalLink,
      heroArticle,
      popularArticles: mostPopular,
      condoPosts: condo,
      housePosts: house,
      townhomePosts: townhome,
      heroVideo: video.heroVideo,
      featuredVideos: video.featuredVideos,
      recommendedLocations,
    };
  } catch (e) {
    console.log(
      'Error when fetching post for property research sub-landing page:',
      e,
    );
  }
};

export default PropertyResearchSubLandingPage;
